import {
  FormControlElement,
  FormControlErrors,
  FormControlValidationEvent,
  FormFieldError,
} from "lib/types";

import { localeMessage } from "lib/i18n";
import { generateFormValidationMessage } from "lib/util/form_validation_messages";
import {
  equalTo,
  missingCheckboxValue,
  missingRadioValue,
  missingValue,
  outOfRange,
  patternMismatch,
  typeMismatch,
  wrongLength,
} from "lib/util/form_validators";

import BaseController from "confinus_ui/base_controller";

const NO_ERRORS: FormControlErrors = {
  missingValue: false,
  equalTo: false,
  outOfRange: false,
  patternMismatch: false,
  typeMismatch: false,
  wrongLength: false,
};

export default abstract class FormFieldController extends BaseController {
  public static targets = [
    "input",
    "container",
    "label",
    "helperText",
    "errorText",
    "errorIcon",
    "errorIconText",
  ];

  public static values = {
    label: String,
    validationMessageRequired: { type: String, default: null },
    validationMessageLengthOver: { type: String, default: null },
    validationMessageLengthUnder: { type: String, default: null },
    validationMessageRangeOver: { type: String, default: null },
    validationMessageRangeUnder: { type: String, default: null },
    validationMessagePatternMismatch: { type: String, default: null },
    validationMessageTypeMismatch: { type: String, default: null },
    validationMessageEqualto: { type: String, default: null },
    // Values from FormControlController for validation
    validateGt: { type: String, default: null },
    validateLt: { type: String, default: null },
    validateType: { type: String, default: null },
    validateEqualTo: { type: String, default: null },
  };

  declare private readonly labelValue: string;
  declare private readonly validationMessageRequiredValue?: string;
  declare private readonly validationMessageLengthOverValue?: string;
  declare private readonly validationMessageLengthUnderValue?: string;
  declare private readonly validationMessageRangeOverValue?: string;
  declare private readonly validationMessageRangeUnderValue?: string;
  declare private readonly validationMessagePatternMismatchValue?: string;
  declare private readonly validationMessageTypeMismatchValue?: string;
  declare private readonly validationMessageEqualtoValue?: string;
  declare private readonly validateGtValue: string;
  declare private readonly validateLtValue: string;
  declare private readonly validateTypeValue: string;
  declare private readonly validateEqualToValue: string;

  // State from FormControlController
  private listening = false;
  private touched = false;

  declare protected readonly inputTarget: HTMLInputElement;

  public connect() {
    super.connect();
    // Auto-enable validation if we're inside a form
    if (this.isInsideForm()) {
      this.autoValidation = true;
    }
  }
  declare protected readonly hasInputTarget: boolean;
  declare protected readonly containerTarget: HTMLDivElement;
  declare protected readonly hasContainerTarget: HTMLDivElement;
  declare protected readonly labelTarget: HTMLDivElement;
  declare protected readonly hasLabelTarget: boolean;
  declare protected readonly helperTextTarget: HTMLParagraphElement;
  declare protected readonly hasHelperTextTarget: boolean;
  declare protected readonly errorTextTarget: HTMLDivElement;
  declare protected readonly hasErrorTextTarget: boolean;
  declare protected readonly errorIconTarget: HTMLDivElement;
  declare protected readonly hasErrorIconTarget: boolean;
  declare protected readonly errorIconTextTarget: HTMLDivElement;
  declare protected readonly hasErrorIconTextTarget: boolean;

  public static classes = [
    "valid",
    "invalid",
    "validInput",
    "invalidInput",
    "validContainer",
    "invalidContainer",
    "validLabel",
    "invalidLabel",
  ];
  declare protected readonly validClasses: string[];
  declare protected readonly invalidClasses: string[];
  declare protected readonly validInputClasses: string[];
  declare protected readonly invalidInputClasses: string[];
  declare protected readonly validContainerClasses: string[];
  declare protected readonly invalidContainerClasses: string[];
  declare protected readonly validLabelClasses: string[];
  declare protected readonly invalidLabelClasses: string[];

  // Can be overridden with the 'invalid' class name modifier for this form field.
  protected get invalidClassName() {
    return "invalid";
  }

  public disconnect() {
    this.autoValidation = false;
    super.disconnect();
  }

  public set autoValidation(shouldAutoValidate: boolean) {
    // By default, a form control will not validate itself on input/blur.
    // This is an opt-in arrangement by calling this setter.
    // Typically this is done by a parent form controller so that
    // form controls are only validated when they are part of a form.
    if (shouldAutoValidate) {
      this.setupEventListeners();
    } else {
      this.teardownEventListeners();
    }
  }

  public handleControlValidatedEvent(evt: FormControlValidationEvent) {
    const {
      target,
      detail: { errors, valid },
    } = evt;
    this.handleValidationResponse(target, valid, errors);
  }

  public get valid() {
    return this.validate().valid;
  }

  public validate() {
    const errors = this.validationErrors;
    const valid = Object.values(errors).every((e) => e === false);

    if (!valid) {
      this.inputTarget.setCustomValidity("invalid");
    }

    this.touched = true;
    this.emitValidationEvent({ errors, valid });

    // Convert the errors into human-readable and return.
    const parsedErrors = this.handleValidationResponse(
      this.inputTarget,
      valid,
      errors,
    );
    return {
      errors: parsedErrors,
      valid,
    };
  }

  public focusControl() {
    this.inputTarget.focus();
  }

  public get name() {
    return this.inputTarget.name;
  }

  public get value() {
    return this.inputTarget.value;
  }

  public set value(val: string) {
    this.inputTarget.value = val;
  }

  public get disabled() {
    return this.inputTarget.hasAttribute("disabled");
  }

  public set disabled(value) {
    if (value) {
      this.inputTarget.setAttribute("disabled", "disabled");
    } else {
      this.inputTarget.removeAttribute("disabled");
    }
    this.toggleTargetElementClasses(this.element, value, [], ["disabled"]);
  }

  public get required() {
    return this.inputTarget.hasAttribute("required");
  }

  public set required(value) {
    if (value) {
      this.inputTarget.setAttribute("required", "required");
    } else {
      this.inputTarget.removeAttribute("required");
    }
  }

  protected set valid(isValid: boolean) {
    this.toggleTargetElementClasses(
      this.element,
      isValid,
      this.invalidClasses,
      this.validClasses,
    );
    if (this.hasContainerTarget) {
      this.toggleTargetElementClasses(
        this.containerTarget,
        isValid,
        this.invalidContainerClasses,
        this.validContainerClasses,
      );
    }
    if (this.hasInputTarget) {
      this.toggleTargetElementClasses(
        this.inputTarget,
        isValid,
        this.invalidInputClasses,
        this.validInputClasses,
      );
    }

    // Merged from FormFieldLayoutController
    this.updateLabelClasses(isValid);
    this.toggleHelperErrorVisibility(isValid);
    this.toggleErrorIcon(!isValid);
  }

  protected generateErrorMessage(
    customMessageKey: string,
    defaultMessageKey: string,
    values = {},
  ) {
    let rawMessage;
    switch (customMessageKey) {
      case "validationMessageRequired":
        rawMessage = this.validationMessageRequiredValue;
        break;
      case "validationMessageLengthOver":
        rawMessage = this.validationMessageLengthOverValue;
        break;
      case "validationMessageLengthUnder":
        rawMessage = this.validationMessageLengthUnderValue;
        break;
      case "validationMessageRangeOver":
        rawMessage = this.validationMessageRangeOverValue;
        break;
      case "validationMessageRangeUnder":
        rawMessage = this.validationMessageRangeUnderValue;
        break;
      case "validationMessagePatternMismatch":
        rawMessage = this.validationMessagePatternMismatchValue;
        break;
      case "validationMessageTypeMismatch":
        rawMessage = this.validationMessageTypeMismatchValue;
        break;
      case "validationMessageEqualto":
        rawMessage = this.validationMessageEqualtoValue;
        break;
      default:
        rawMessage = null;
    }

    return generateFormValidationMessage(
      rawMessage || this.generateDefaultMessage(defaultMessageKey),
      values,
    );
  }

  // Validation logic merged from FormControlController

  private get validationErrors(): FormControlErrors {
    if (this.disabled) {
      // If the form field is disabled, it won't be sent to the server.
      // So, let's not validate it.
      return { ...NO_ERRORS };
    }

    return {
      missingValue: this.missingValueError,
      equalTo: this.equalToError,
      outOfRange: this.outOfRangeError,
      patternMismatch: this.patternMismatchError,
      typeMismatch: this.typeMismatchError,
      wrongLength: this.wrongLengthError,
    };
  }

  private emitValidationEvent({
    errors,
    valid,
  }: {
    errors: FormControlErrors;
    valid: boolean;
  }) {
    this.dispatch("validated", {
      bubbles: true,
      cancelable: false,
      detail: {
        errors,
        valid,
      },
    });
  }

  private setupEventListeners() {
    if (this.listening || !this.hasInputTarget) {
      return;
    }

    this.inputTarget.addEventListener("input", this.handleInputEvent);
    this.inputTarget.addEventListener("focus", this.handleFocusEvent);
    this.inputTarget.addEventListener("blur", this.handleBlurEvent);

    // Listen for changes on the equalTo target field so we re-validate when it changes
    const equalToTarget = this.equalToTargetElement;
    if (equalToTarget) {
      equalToTarget.addEventListener("input", this.handleEqualToTargetInput);
    }

    this.listening = true;
  }

  private teardownEventListeners() {
    if (!this.listening || !this.hasInputTarget) {
      return;
    }

    this.inputTarget.removeEventListener("input", this.handleInputEvent);
    this.inputTarget.removeEventListener("blur", this.handleBlurEvent);

    const equalToTarget = this.equalToTargetElement;
    if (equalToTarget) {
      equalToTarget.removeEventListener("input", this.handleEqualToTargetInput);
    }

    this.listening = false;
  }

  private handleInputEvent = () => {
    if (this.touched) {
      this.validate();
    }
  };

  private handleFocusEvent = () => {
    if (this.inputTarget.value) {
      this.touched = true;
    }
  };

  private handleBlurEvent = () => {
    this.validate();
  };

  private handleEqualToTargetInput = () => {
    if (this.touched) {
      this.validate();
    }
  };

  private get equalToTargetElement(): HTMLInputElement | null {
    const id =
      this.validateEqualToValue ||
      this.inputTarget.getAttribute(
        "data-form-control-validate-equal-to-value",
      );
    if (!id) return null;
    let el = document.getElementById(id);
    if (!el) return null;
    // If the target is a wrapper, find the input within it
    if (!(el instanceof HTMLInputElement)) {
      el = el.querySelector("input");
    }
    return el as HTMLInputElement | null;
  }

  private get missingValueError() {
    const el = this.inputTarget;
    switch (el.type) {
      case "radio": {
        const inputGroup = Array.from(document.getElementsByTagName("input"));
        const radioGroup = inputGroup.filter(
          (input) => input.getAttribute("name") === el.getAttribute("name"),
        );
        const radioAttributes = radioGroup.map(this.mapCheckableAttributes);
        return missingRadioValue(radioAttributes);
      }
      case "checkbox": {
        const checkboxAttributes = this.mapCheckableAttributes(
          el as HTMLInputElement,
        );
        return missingCheckboxValue(checkboxAttributes);
      }
      default:
        return missingValue({
          required: el.hasAttribute("required"),
          value: el.value,
        });
    }
  }

  private get outOfRangeError() {
    const el = this.inputTarget;
    const attributes = {
      gt: this.validateGtValue,
      lt: this.validateLtValue,
      max: el.getAttribute("max"),
      min: el.getAttribute("min"),
      value: el.value,
    };
    return outOfRange(attributes);
  }

  private get patternMismatchError() {
    const el = this.inputTarget;
    const attributes = {
      pattern: el.getAttribute("pattern"),
      type: el.type,
      value: el.value,
    };
    return patternMismatch(attributes);
  }

  private get wrongLengthError() {
    const el = this.inputTarget;
    const attributes = {
      max: el.getAttribute("maxlength"),
      min: el.getAttribute("minlength"),
      value: el.value,
    };
    return wrongLength(attributes);
  }

  private get equalToError() {
    const el = this.inputTarget;
    const attributes = {
      value: el.value,
      equalToId:
        this.validateEqualToValue ||
        el.getAttribute("data-form-control-validate-equal-to-value"),
    };
    return equalTo(attributes);
  }

  private get typeMismatchError() {
    const el = this.inputTarget;
    const attributes = {
      type: this.validateTypeValue,
      value: el.value,
    };
    return typeMismatch(attributes);
  }

  private mapCheckableAttributes(field: HTMLInputElement) {
    return {
      checked: field.checked,
      required: field.hasAttribute("required"),
    };
  }

  private isInsideForm(): boolean {
    return (
      this.element.closest(
        'form[data-controller*="confinus-ui--forms--form"]',
      ) !== null
    );
  }

  private handleValidationResponse(
    fieldControlTarget: FormControlElement,
    isValid: boolean,
    errors: FormControlErrors,
  ) {
    this.valid = isValid;
    const parsedErrors = this.parseErrorMessages(errors);

    // Set error text directly (merged from FormFieldLayoutController)
    const errorMessage =
      parsedErrors.length > 0 ? parsedErrors[0].message : null;
    this.setErrorText(errorMessage);
    this.toggleHelperErrorVisibility(errorMessage === null);
    this.toggleErrorIcon(errorMessage !== null);

    // Push out an event that a higher controller can respond to
    this.emitFormFieldValidatedEvent(fieldControlTarget, isValid, parsedErrors);

    return parsedErrors;
  }

  private parseErrorMessages(errors: FormControlErrors): FormFieldError[] {
    const errorResponse = [];
    if (errors.missingValue) {
      errorResponse.push({
        code: "missingValue",
        message: this.generateErrorMessage(
          "validationMessageRequired",
          "blank",
        ),
      });
    }
    if (errors.wrongLength === "over") {
      errorResponse.push({
        code: "lengthOver",
        message: this.generateErrorMessage(
          "validationMessageLengthOver",
          "invalid",
        ),
      });
    }
    if (errors.wrongLength === "under") {
      errorResponse.push({
        code: "lengthUnder",
        message: this.generateErrorMessage(
          "validationMessageLengthUnder",
          "invalid",
        ),
      });
    }
    if (errors.outOfRange === "over") {
      errorResponse.push({
        code: "rangeOver",
        message: this.generateErrorMessage(
          "validationMessageRangeOver",
          "invalid",
        ),
      });
    }
    if (errors.outOfRange === "under") {
      errorResponse.push({
        code: "rangeUnder",
        message: this.generateErrorMessage(
          "validationMessageRangeUnder",
          "invalid",
        ),
      });
    }
    // Pattern matching comes last.
    // Numerical values often have a pattern attribute to bring up the correct on-screen keyboard,
    // but in many cases we'd rather the (more specific) range message be displayed first.
    if (errors.patternMismatch) {
      errorResponse.push({
        code: "patternMismatch",
        message: this.generateErrorMessage(
          "validationMessagePatternMismatch",
          "invalid",
        ),
      });
    }
    if (errors.typeMismatch) {
      errorResponse.push({
        code: "typeMismatch",
        message: this.generateErrorMessage(
          "validationMessageTypeMismatch",
          "invalid",
        ),
      });
    }
    if (errors.equalTo) {
      errorResponse.push({
        code: "equalTo",
        message: this.generateErrorMessage(
          "validationMessageEqualto",
          "does_not_match",
        ),
      });
    }
    return errorResponse;
  }

  private generateDefaultMessage(key: string) {
    return `${this.label} ${localeMessage(key)}`;
  }

  private get label() {
    const label = this.labelValue;
    if (!label) {
      throw new Error(`${this.identifier} should define a label attribute`);
    }
    return label;
  }

  private emitFormFieldValidatedEvent(
    target: FormControlElement,
    valid: boolean,
    errors: FormFieldError[],
  ) {
    this.dispatch("validated", {
      target,
      bubbles: true,
      cancelable: false,
      detail: {
        errors,
        valid,
      },
    });
  }

  // Private methods merged from FormFieldLayoutController

  private updateLabelClasses(isValid: boolean) {
    if (this.hasLabelTarget) {
      this.toggleTargetElementClasses(
        this.labelTarget,
        isValid,
        this.invalidLabelClasses,
        this.validLabelClasses,
      );
    }
  }

  /**
   * Set error text in both helper text section and error icon section.
   * Merged from HelperTextSectionController and ErrorIconSectionController.
   */
  private setErrorText(errorMessage: string | null) {
    // Update helper text section error text
    if (this.hasErrorTextTarget && errorMessage) {
      this.errorTextTarget.textContent = errorMessage;
    }

    // Update error icon section error text
    if (this.hasErrorIconTextTarget && errorMessage) {
      this.errorIconTextTarget.textContent = errorMessage;
    }
  }

  /**
   * Toggle between helper text and error text visibility.
   * Merged from HelperTextSectionController.
   */
  private toggleHelperErrorVisibility(showHelper: boolean) {
    // Toggle helper text visibility
    if (this.hasHelperTextTarget) {
      const hasContent =
        this.helperTextTarget.textContent &&
        this.helperTextTarget.textContent.trim() !== "";
      this.toggleTargetElementClasses(
        this.helperTextTarget,
        showHelper && !!hasContent,
        ["hidden"],
        [],
      );
    }

    // Toggle error text visibility (inverse of helper)
    if (this.hasErrorTextTarget) {
      this.toggleTargetElementClasses(
        this.errorTextTarget,
        showHelper,
        [],
        ["hidden"],
      );
    }
  }

  /**
   * Show or hide the error icon section.
   * Merged from ErrorIconSectionController.
   */
  private toggleErrorIcon(show: boolean) {
    if (this.hasErrorIconTarget) {
      this.toggleTargetElementClasses(
        this.errorIconTarget,
        show,
        ["hidden"],
        [],
      );
    }
  }
}
